/**
 * Local achievement unlocks. HUD count is derived from this file only.
 */

export type AchievementId =
  | "jump_air"
  | "secret_orbit"
  | "konami"
  | "night_owl"
  | "plaza_sprinter"
  | "visit_cnnrc"
  | "visit_boorhub"
  | "visit_as133555"
  | "visit_edge"
  | "visit_all"
  | "first_probe"
  | "gate_seal";

export interface Achievement {
  id: AchievementId;
  en: string;
  zh: string;
  unlockedAt?: string;
}

export const ACHIEVEMENT_LABEL: Record<AchievementId, { en: string; zh: string }> = {
  jump_air: { en: "Airborne", zh: "腾空" },
  secret_orbit: { en: "Secret Orbit", zh: "密轨" },
  konami: { en: "Konami", zh: "秘码" },
  night_owl: { en: "Night Owl", zh: "夜枭" },
  plaza_sprinter: { en: "Plaza Sprinter", zh: "飞跑" },
  visit_cnnrc: { en: "CNNRC", zh: "CNNRC" },
  visit_boorhub: { en: "Boorhub", zh: "Boorhub" },
  visit_as133555: { en: "AS133555", zh: "AS133555" },
  visit_edge: { en: "At the Edge", zh: "抵达边缘" },
  visit_all: { en: "Full Plaza", zh: "四区走完" },
  first_probe: { en: "First Probe", zh: "首次探测" },
  gate_seal: { en: "Gate Seal", zh: "门印" },
};

export const ACHIEVEMENT_TOTAL = Object.keys(ACHIEVEMENT_LABEL).length;

const UNLOCKED_KEY = "doorpost.achievements";

function loadUnlocked(): Set<AchievementId> {
  try {
    const raw = localStorage.getItem(UNLOCKED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is AchievementId => id in ACHIEVEMENT_LABEL));
  } catch {
    return new Set();
  }
}

function saveUnlocked(set: Set<AchievementId>): void {
  localStorage.setItem(UNLOCKED_KEY, JSON.stringify([...set]));
}

export function listAchievements(): Achievement[] {
  const unlocked = loadUnlocked();
  return (Object.keys(ACHIEVEMENT_LABEL) as AchievementId[]).map((id) => ({
    id,
    en: ACHIEVEMENT_LABEL[id].en,
    zh: ACHIEVEMENT_LABEL[id].zh,
    unlockedAt: unlocked.has(id) ? "1" : undefined,
  }));
}

export function unlockedCount(): number {
  return loadUnlocked().size;
}

export function unlock(id: AchievementId): boolean {
  const set = loadUnlocked();
  if (set.has(id)) return false;
  set.add(id);
  saveUnlocked(set);
  document.dispatchEvent(new CustomEvent("doorpost:achievement", { detail: { id } }));
  return true;
}

const ZONE_ACH: Record<string, AchievementId> = {
  cnnrc: "visit_cnnrc",
  boorhub: "visit_boorhub",
  as133555: "visit_as133555",
  edge: "visit_edge",
};

export function noteZoneVisit(zone: string): void {
  const id = ZONE_ACH[zone];
  if (id) unlock(id);
  const have = loadUnlocked();
  if (
    have.has("visit_cnnrc") &&
    have.has("visit_boorhub") &&
    have.has("visit_as133555") &&
    have.has("visit_edge")
  ) {
    unlock("visit_all");
  }
}

export function noteFirstProbe(): void {
  unlock("first_probe");
}
